import { useState } from "react";
import { X, MessageCircle, QrCode, Smartphone, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { cn } from "../lib/utils";

interface WhatsAppConnectModalProps {
  open: boolean;
  onClose: () => void;
  status: string;
  onConnect: (phone: string, useQr: boolean) => Promise<void>;
  onDisconnect: () => Promise<void>;
  loading: boolean;
  pairingCode: string;
  qrCodeStr: string;
}

export function WhatsAppConnectModal({
  open,
  onClose,
  status,
  onConnect,
  onDisconnect,
  loading,
  pairingCode,
  qrCodeStr,
}: WhatsAppConnectModalProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [useQr, setUseQr] = useState(true);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="font-semibold text-lg">
            {status === "connected" ? "WhatsApp Status" : "Connect WhatsApp"}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {status === "connected" ? (
            <div className="flex flex-col items-center py-4 text-center">
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="w-10 h-10" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Successfully Connected
              </h3>
              <p className="text-slate-500 text-sm mt-1 mb-6">
                Your WhatsApp account is active and ready to use.
              </p>
              <button
                onClick={onDisconnect}
                disabled={loading}
                className="w-full bg-red-50 text-red-600 font-medium py-2.5 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  "Log out of WhatsApp"
                )}
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
                <button
                  className={cn(
                    "flex-1 py-2 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2",
                    useQr
                      ? "bg-white shadow text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  )}
                  onClick={() => setUseQr(true)}
                >
                  <QrCode className="w-4 h-4" />
                  QR Code
                </button>
                <button
                  className={cn(
                    "flex-1 py-2 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2",
                    !useQr
                      ? "bg-white shadow text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  )}
                  onClick={() => setUseQr(false)}
                >
                  <Smartphone className="w-4 h-4" />
                  Phone Number
                </button>
              </div>

              {!useQr ? (
                <>
                  <p className="text-sm text-slate-600">
                    Enter your phone number including country code to get a
                    pairing code.
                  </p>
                  <input
                    type="text"
                    placeholder="e.g. 14155552671"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full border-slate-200 rounded-lg border px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
                  />
                  <button
                    onClick={() => onConnect(phoneNumber, false)}
                    disabled={loading || !phoneNumber}
                    className="w-full bg-green-600 text-white font-medium py-2.5 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Requesting...
                      </>
                    ) : (
                      "Get Pairing Code"
                    )}
                  </button>

                  {pairingCode && (
                    <div className="mt-4 p-5 bg-slate-50 rounded-xl text-center border">
                      <p className="text-xs text-slate-500 mb-3">
                        Enter this code in WhatsApp {" > "} Linked Devices {" > "}
                        Link with phone number
                      </p>
                      <p className="text-3xl font-mono tracking-[0.25em] font-bold text-slate-900">
                        {pairingCode}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center">
                  <p className="text-sm text-slate-600 mb-4 text-center">
                    Open WhatsApp on your phone, go to Linked Devices, and scan
                    this QR code.
                  </p>

                  {qrCodeStr ? (
                    <div className="p-5 bg-white border rounded-xl shadow-sm">
                      <QRCodeSVG value={qrCodeStr} size={200} />
                    </div>
                  ) : (
                    <button
                      onClick={() => onConnect("", true)}
                      disabled={loading}
                      className="w-full bg-green-600 text-white font-medium py-2.5 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        "Generate QR Code"
                      )}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
